import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import omit from 'lodash/omit';
import { isBrowserAuthConnection } from 'vet-bones/utils';

import { ROUTE } from 'src/constants/routes';
import { MappingField } from 'src/containers/ProjectDiagram/types/data';
import {
  connectionAsyncActions,
  connectionsSlice,
} from 'src/store/connections/connections.slice';
import {
  EntityDrawer,
  EntityDrawerType,
  ModelListTab,
  ProjectSortOption,
  UiState,
  createInitialUiState,
} from 'src/store/ui/types';

export const uiSlice = createSlice({
  name: 'ui',
  initialState: createInitialUiState,
  reducers: {
    updatedMappingFilters: (
      state,
      action: PayloadAction<{
        projectId: string;
        mappingId: string;
        filtersByIri: Record<string, boolean>;
      }>
    ) => {
      const { projectId, mappingId, filtersByIri } = action.payload;
      if (!state.filtersByProjectMapping[projectId]) {
        state.filtersByProjectMapping[projectId] = {};
      }
      state.filtersByProjectMapping[projectId][mappingId] = {
        ...state.filtersByProjectMapping[projectId][mappingId],
        ...filtersByIri,
      };
    },
    updatedSaveState: (state, action: PayloadAction<boolean>) => {
      state.isSavingProject = action.payload;
    },
    updatedMappingFiltersIri: (
      state,
      action: PayloadAction<{
        projectId: string;
        prevIri: string;
        nextIri: string;
      }>
    ) => {
      const { projectId, prevIri, nextIri } = action.payload;
      Object.entries(state.filtersByProjectMapping[projectId] || {}).forEach(
        ([mappingId, filtersByIri]) => {
          const prevValue = filtersByIri[prevIri];
          if (prevValue === undefined) {
            return;
          }
          filtersByIri[nextIri] = prevValue;
          state.filtersByProjectMapping[projectId][mappingId] = omit(
            filtersByIri,
            prevIri
          );
        }
      );
    },
    changedEntityDrawer: (
      state,
      action: PayloadAction<{ drawer: EntityDrawer; projectId: string }>
    ) => {
      const prevDrawer = state.entityDrawer;
      const { drawer, projectId } = action.payload;

      if (drawer.entityValue === prevDrawer.entityValue) {
        return;
      }

      let history: EntityDrawer[];

      if (
        drawer.type === EntityDrawerType.NONE ||
        projectId !== prevDrawer.projectId
      ) {
        history = [];
      } else {
        history = [
          ...prevDrawer.history,
          {
            entityValue: prevDrawer.entityValue,
            type: prevDrawer.type,
          },
        ];
      }

      state.entityDrawer = {
        ...drawer,
        history,
        projectId,
      };
      state.ruleOverviewDrawer = {
        isOpen: false,
      };
    },
    changedLocation: (state, action: PayloadAction<{ route: ROUTE }>) => {
      const initialState = createInitialUiState();
      initialState.isVoiceboxDrawerOpen = state.isVoiceboxDrawerPendingOpen;
      initialState.isVoiceboxDrawerSummaryOpen =
        state.isVoiceboxDrawerSummaryPendingOpen;
      initialState.isProjectModelDialogOpen =
        state.isProjectModelDialogPendingOpen;
      initialState.isProjectResourceDialogOpen =
        state.isProjectResourceDialogPendingOpen;
      // Preserve create project overlay state during navigation (for generate model flow)
      initialState.isCreateProjectOverlayOpen =
        state.isCreateProjectOverlayOpen;
      initialState.isBrowserAuthFailedDialogOpen =
        state.isBrowserAuthFailedDialogOpen;
      initialState.isSignInDialogOpen = state.isSignInDialogOpen;
      if (
        state.entityDrawer.projectId &&
        action.payload.route.includes(state.entityDrawer.projectId) &&
        !action.payload.route.includes('/mapping/')
      ) {
        initialState.entityDrawer = state.entityDrawer;
      }
      if (
        state.modelList.projectId &&
        action.payload.route.includes(state.modelList.projectId)
      ) {
        initialState.modelList = state.modelList;
      }
      return initialState;
    },
    changeProjectSortOption: (
      state,
      action: PayloadAction<ProjectSortOption>
    ) => {
      state.projectSortOption = action.payload;
    },
    closedSignInDialog: (state) => {
      state.isSignInDialogOpen = false;
    },
    openedBrowserAuthFailedDialog: (state) => {
      state.isBrowserAuthFailedDialogOpen = true;
    },
    openedSignInDialog: (state) => {
      state.isSignInDialogOpen = true;
    },
    removedKeysFromEntityDrawerHistory: (
      { entityDrawer },
      action: PayloadAction<{ entityValues: string[]; projectId: string }>
    ) => {
      const { entityValues, projectId } = action.payload;
      if (projectId !== entityDrawer.projectId) {
        return;
      }

      const newHistory = entityDrawer.history.filter(
        (drawerEntry) =>
          drawerEntry.entityValue &&
          !entityValues.includes(drawerEntry.entityValue)
      );

      const [newValue, newType] = entityValues.includes(
        entityDrawer.entityValue || ''
      )
        ? [undefined, EntityDrawerType.NONE]
        : [entityDrawer.entityValue, entityDrawer.type];

      Object.assign(entityDrawer, {
        entityValue: newValue,
        history: newHistory,
        type: newType,
      });
    },
    selectedMappingDialogClass: (
      state,
      action: PayloadAction<{
        toValue?: string;
      }>
    ) => {
      const { toValue } = action.payload;
      state.createMappingDialog = {
        ...state.createMappingDialog,
        toValue: state.createMappingDialog.isOpen ? toValue : undefined,
      };
    },
    selectedModelListTab: (state, action: PayloadAction<ModelListTab>) => {
      state.modelList.tabId = action.payload;
    },
    selectedPreviousEntityDrawer: (
      state,
      action: PayloadAction<{ projectId: string; entityValue: string }>
    ) => {
      if (action.payload.projectId !== state.entityDrawer.projectId) {
        return;
      }

      const history = [...state.entityDrawer.history];

      let entry: EntityDrawer | null = null;
      while (!entry && history.length) {
        const tempEntry = history.pop() as EntityDrawer;
        if (tempEntry.entityValue === action.payload.entityValue) {
          entry = tempEntry;
        }
      }

      if (!entry) {
        return;
      }

      state.entityDrawer = {
        ...entry,
        history,
        projectId: action.payload.projectId,
      };
    },
    toggledCreateClassDrawer: (
      state,
      { payload }: PayloadAction<{ isOpen: boolean; initialValue?: string }>
    ) => {
      const { isOpen, initialValue } = payload;
      state.createClassDrawer = {
        isOpen,
        initialValue: isOpen ? initialValue : undefined,
      };
    },
    toggledCreateMappingDialog: (
      state,
      action: PayloadAction<{
        initialValue?: string;
        isOpen: boolean;
        fromId?: string;
        fields?: Record<string, MappingField>;
        toValue?: string;
      }>
    ) => {
      const { initialValue, isOpen, fromId, fields, toValue } = action.payload;
      state.createMappingDialog = {
        initialValue: isOpen ? initialValue : undefined,
        isOpen,
        fromId: isOpen ? fromId : undefined,
        fields: isOpen ? fields : undefined,
        toValue: isOpen ? toValue : undefined,
      };
    },
    toggledCreateProjectOverlay: (
      state,
      { payload }: PayloadAction<{ isOpen: boolean }>
    ) => {
      state.isCreateProjectOverlayOpen = payload.isOpen;
    },
    toggledCreateRelationshipDrawer: (
      state,
      {
        payload,
      }: PayloadAction<{
        isOpen: boolean;
        sourceClassIri?: string;
        targetClassIri?: string;
      }>
    ) => {
      const { isOpen, sourceClassIri, targetClassIri } = payload;
      state.createRelationshipDrawer = {
        isOpen,
        sourceClassIri: isOpen ? sourceClassIri : undefined,
        targetClassIri: isOpen ? targetClassIri : undefined,
      };
    },
    toggledCreateSensitivePropertyGroupDrawer: (
      state,
      action: PayloadAction<{ isOpen: boolean; attributeIri?: string }>
    ) => {
      const { isOpen, attributeIri } = action.payload;
      state.createSensitivePropertyGroupDrawer = {
        isOpen,
        attributeIri: isOpen ? attributeIri : undefined,
      };
    },
    toggledFileUploadPreviewDialog: (
      state,
      action: PayloadAction<{
        isOpen: boolean;
        resourceId?: string;
      }>
    ) => {
      const { isOpen, resourceId } = action.payload;
      state.fileUploadPreviewDialog = {
        isOpen,
        resourceId: isOpen ? resourceId : undefined,
      };
    },
    toggledModelList: (
      state,
      action: PayloadAction<{ isOpen?: boolean; projectId: string }>
    ) => {
      state.modelList.projectId = action.payload.projectId;
      state.modelList.isOpen = action.payload.isOpen ?? !state.modelList.isOpen;
    },
    toggledProjectMappingSettingsDrawer: (
      state,
      action: PayloadAction<{ isOpen: boolean; mappingId?: string }>
    ) => {
      const { isOpen, mappingId } = action.payload;
      state.projectMappingSettingsDrawer = {
        isOpen,
        mappingId: isOpen ? mappingId : undefined,
      };
    },
    toggledProjectModelDialog: (
      state,
      {
        payload: { isOpen, isPendingOpen },
      }: PayloadAction<{ isOpen?: boolean; isPendingOpen?: boolean }>
    ) => {
      if (isOpen !== undefined) {
        state.isProjectModelDialogOpen = isOpen;
      }
      if (isPendingOpen !== undefined) {
        state.isProjectModelDialogPendingOpen = isPendingOpen;
      }
    },
    toggledProjectResourceDialog: (
      state,
      {
        payload: { isOpen, isPendingOpen },
      }: PayloadAction<{ isOpen?: boolean; isPendingOpen?: boolean }>
    ) => {
      if (isOpen !== undefined) {
        state.isProjectResourceDialogOpen = isOpen;
      }
      if (isPendingOpen !== undefined) {
        state.isProjectResourceDialogPendingOpen = isPendingOpen;
      }
    },
    toggledProjectSettingsDrawer: (
      state,
      { payload }: PayloadAction<{ isOpen: boolean }>
    ) => {
      state.isProjectSettingsDrawerOpen = payload.isOpen;
    },
    toggledPublishProjectDrawer: (
      state,
      { payload }: PayloadAction<{ isOpen: boolean }>
    ) => {
      state.isPublishProjectDrawerOpen = payload.isOpen;
    },
    toggledRenameProjectModelDialog: (
      state,
      { payload }: PayloadAction<{ isOpen: boolean; modelId?: string }>
    ) => {
      const { isOpen, modelId } = payload;
      state.renameProjectModelDialog = {
        isOpen,
        modelId: isOpen ? modelId : undefined,
      };
    },
    toggledResourceSuggestionsDialog: (
      state,
      action: PayloadAction<UiState['resourceSuggestionsDialog']>
    ) => {
      state.resourceSuggestionsDialog = action.payload;
    },
    toggledRuleDetailDrawer: (
      state,
      { payload }: PayloadAction<{ isOpen: boolean; ruleId?: string }>
    ) => {
      const { isOpen, ruleId } = payload;
      state.ruleDetailDrawer = {
        isOpen,
        ruleId: isOpen ? ruleId : undefined,
      };
    },
    toggledRuleOverviewDrawer: (
      state,
      {
        payload,
      }: PayloadAction<{
        isOpen: boolean;
      }>
    ) => {
      state.entityDrawer = {
        type: EntityDrawerType.NONE,
        history: [],
        projectId: null,
      };
      state.ruleOverviewDrawer = payload;
    },
    toggledVirtualGraphPreviewDialog: (
      state,
      {
        payload: { isOpen, resourceId },
      }: PayloadAction<{
        isOpen: boolean;
        resourceId?: string;
      }>
    ) => {
      state.virtualGraphPreviewDialog = {
        isOpen,
        resourceId: isOpen ? resourceId : undefined,
      };
    },
    toggledVoiceboxDrawer: (
      state,
      {
        payload: { isOpen, isPendingOpen },
      }: PayloadAction<{ isOpen?: boolean; isPendingOpen?: boolean }>
    ) => {
      if (isOpen !== undefined) {
        state.isVoiceboxDrawerOpen = isOpen;
      }
      if (isPendingOpen !== undefined) {
        state.isVoiceboxDrawerPendingOpen = isPendingOpen;
      }
    },
    toggledVoiceboxDrawerSummary: (
      state,
      {
        payload: { isOpen, isPendingOpen },
      }: PayloadAction<{ isOpen?: boolean; isPendingOpen?: boolean }>
    ) => {
      if (isOpen !== undefined) {
        state.isVoiceboxDrawerSummaryOpen = isOpen;
      }
      if (isPendingOpen !== undefined) {
        state.isVoiceboxDrawerSummaryPendingOpen = isPendingOpen;
      }
    },
    updatedEntityDrawerValue: (
      state,
      action: PayloadAction<{
        projectId: string;
        prevEntityValue: string;
        entityValue: string;
      }>
    ) => {
      const { prevEntityValue, projectId, entityValue } = action.payload;
      if (projectId !== state.entityDrawer.projectId) {
        return;
      }

      const history = [...state.entityDrawer.history].map((drawerEntry) => ({
        ...drawerEntry,
        entityValue:
          drawerEntry.entityValue === prevEntityValue
            ? entityValue
            : drawerEntry.entityValue,
      }));

      Object.assign(state.entityDrawer, {
        entityValue,
        history,
      });
    },
    updatedModelListTableData: <T extends ModelListTab>(
      state: UiState,
      action: PayloadAction<{
        tab: T;
        data: UiState['modelList']['tableData'][T];
      }>
    ) => {
      state.modelList.tableData[action.payload.tab] = action.payload.data;
    },
    voiceboxFeedbackFailed: (
      _state,
      _action: PayloadAction<{
        message: string;
      }>
    ) => {},
    voiceboxModelingAndMappingFailed: (
      _state,
      _action: PayloadAction<{
        message: string;
      }>
    ) => {},
  },
  extraReducers: ({ addCase }) => {
    addCase(
      connectionsSlice.actions.signedOut,
      (state, { payload: { connection, showDialog = true } }) => {
        state.isBrowserAuthFailedDialogOpen = Boolean(
          showDialog && isBrowserAuthConnection(connection)
        );
        state.isSignInDialogOpen =
          showDialog && !isBrowserAuthConnection(connection);
      }
    );
    addCase(
      connectionAsyncActions.requestValidateConnection.fulfilled,
      (state) => {
        state.isSignInDialogOpen = false;
      }
    );
  },
});
